import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronRight, LogOut, Menu, Moon, Settings, Sun, X } from "lucide-react";

import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { PERMISO, nombreRol } from "../sesion/permisos.ts";
import { navegacionPara } from "./navegacion.ts";
import { usarPasosDeInicio } from "../paginas/Empezar.tsx";
import { usarLibro } from "../ganchos/datos.ts";
import { usarTema } from "../ganchos/tema.ts";
import { inicial } from "../formato.ts";
import { MarcaCompleta } from "./Marca.tsx";
import { Boton, cx } from "./ui.tsx";

export function Marco() {
  const { rol, cuenta, cuentaNombre, usuario, salir, puedo } = usarSesion();
  const { tema, alternar } = usarTema();
  const { data: libro } = usarLibro();
  const [abierto, setAbierto] = useState(false);
  const ubicacion = useLocation();
  // Empezar sale del menú cuando están los cinco pasos (SPEC-029 §15). Solo se
  // pregunta por quien configura: a los demás no se les enseña nunca.
  const inicio = usarPasosDeInicio(false, puedo(PERMISO.CONFIGURAR));
  const inicioCompleto = inicio.listo && inicio.cuantos >= 5;

  // Navegar cierra el cajón en móvil. Sin esto queda tapando la pantalla a la
  // que el usuario acaba de pedir ir.
  useEffect(() => setAbierto(false), [ubicacion.pathname]);

  const contadores = {
    pendientes: libro?.resumen.pendientes ?? 0,
    alertas: libro?.alertas.length ?? 0,
  };

  const lateral = (
    <div className="flex h-full flex-col gap-6 p-4">
      <div className="flex items-center justify-between px-2 pt-1">
        <MarcaCompleta />
        <button
          onClick={() => setAbierto(false)}
          className="rounded-lg p-1.5 text-tenue hover:bg-superficie-2 lg:hidden"
          aria-label="Cerrar menú"
        >
          <X className="size-5" aria-hidden />
        </button>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto">
        {/* El filtro por rol vive en `navegacion.ts` y no aquí: es lo que
            comprueba CA-12, y una prueba sobre una copia no prueba la que
            corre. */}
        {navegacionPara(rol, { inicioCompleto }).map(({ grupo, plegado, entradas: visibles }) => {
          const enlaces = visibles.map(({ a, texto, icono: Icono, cuenta: contar }) => {
                const n = contar?.(contadores) ?? 0;
                return (
                  <NavLink
                    key={a}
                    to={a}
                    end={a === "/"}
                    className={({ isActive }) =>
                      cx(
                        "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-marca-suave text-marca-tinta"
                          : "text-tenue hover:bg-superficie-2 hover:text-tinta",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icono
                          className={cx("size-4.5 shrink-0", isActive && "text-marca")}
                          aria-hidden
                        />
                        <span className="flex-1 truncate">{texto}</span>
                        {n > 0 && (
                          <span className="cifra rounded-full bg-aviso px-1.5 py-0.5 text-[11px] leading-none font-semibold text-white">
                            {n > 99 ? "99+" : n}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              });
          return plegado ? (
            <GrupoPlegado
              key={grupo}
              titulo={grupo}
              dentro={visibles.some((e) => ubicacion.pathname.startsWith(e.a))}
            >
              {enlaces}
            </GrupoPlegado>
          ) : (
            <div key={grupo} className="space-y-1">
              <p className="px-3 pb-1 text-[11px] font-semibold tracking-wider text-tenue-2 uppercase">
                {grupo}
              </p>
              {enlaces}
            </div>
          );
        })}
      </nav>

      <div className="space-y-3 border-t border-borde pt-4">
        {/* Quién está dentro, y solo después en qué cuenta.
            Antes aquí solo iba el nombre de la cuenta, así que en un equipo de
            cinco personas la pantalla no decía con qué usuario estabas. Y quien
            firma las decisiones es la persona, no la empresa. */}
        {/* Y es la puerta a «Mi cuenta» (SPEC-029 §16): la contraseña y el
            nombre de uno se buscan en el nombre de uno, no en el menú. */}
        <NavLink
          to="/cuenta"
          title="Mi cuenta"
          className={({ isActive }) =>
            cx(
              "group flex items-center gap-3 rounded-xl px-1 py-1 transition-colors",
              isActive ? "bg-marca-suave" : "hover:bg-superficie-2",
            )
          }
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-marca text-sm font-semibold text-white">
            {inicial(usuario?.nombre ?? usuario?.correo ?? cuenta ?? "4drando")}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium" title={usuario?.correo}>
              {usuario?.nombre || usuario?.correo || "Tu sesión"}
            </p>
            <p className="truncate text-xs text-tenue">
              {nombreRol(rol)}
              {cuenta ? ` · ${cuentaNombre || cuenta}` : ""}
            </p>
          </div>
          <span className="pr-1 text-[11px] font-medium text-tenue-2 group-hover:text-tinta">Mi cuenta</span>
        </NavLink>
        <div className="flex gap-2">
          <Boton
            variante="fantasma"
            tamano="sm"
            icono={tema === "oscuro" ? Sun : Moon}
            onClick={alternar}
            className="flex-1"
          >
            {tema === "oscuro" ? "Claro" : "Oscuro"}
          </Boton>
          <Boton
            variante="fantasma"
            tamano="sm"
            icono={LogOut}
            onClick={() => void salir()}
            className="flex-1"
          >
            Salir
          </Boton>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[17rem_1fr]">
      {/* Barra lateral fija en escritorio. */}
      <aside className="sticky top-0 hidden h-dvh border-r border-borde bg-superficie lg:block">
        {lateral}
      </aside>

      {/* Cajón en móvil. */}
      {abierto && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            onClick={() => setAbierto(false)}
            aria-label="Cerrar menú"
          />
          <aside className="absolute inset-y-0 left-0 w-[17rem] border-r border-borde bg-superficie shadow-flotante subir">
            {lateral}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-borde bg-superficie/85 px-4 py-3 backdrop-blur-md lg:hidden">
          <button
            onClick={() => setAbierto(true)}
            className="rounded-lg p-1.5 text-tenue hover:bg-superficie-2 hover:text-tinta"
            aria-label="Abrir menú"
          >
            <Menu className="size-5" aria-hidden />
          </button>
          <MarcaCompleta className="flex-1" />
          {contadores.pendientes > 0 && (
            <NavLink
              to="/bandeja"
              className="cifra rounded-full bg-aviso px-2 py-1 text-xs font-semibold text-white"
            >
              {contadores.pendientes}
            </NavLink>
          )}
        </header>

        {/* max-w-6xl (1152px) dejaba ~250px muertos a cada lado en una pantalla
            de 1600, y el precio no era estético: los nombres de las tablas se
            cortaban —"COMERCIALIZADORA TO…"— mientras sobraba sitio al lado.
            96rem tampoco es «sin límite»: en un monitor muy ancho, una fila de
            2500px obliga a recorrer con la vista de un extremo a otro para no
            perder el renglón. */}
        <main className="mx-auto w-full max-w-[96rem] flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
          <Outlet />
        </main>

        <footer className="mx-auto flex w-full max-w-[96rem] items-center gap-2 px-4 pb-8 text-xs text-tenue-2 sm:px-6 lg:px-8">
          <Settings className="size-3.5" aria-hidden />
          <span>
            4drando decide con evidencia. Cuando no le alcanza, pregunta — nunca adivina.
          </span>
        </footer>
      </div>
    </div>
  );
}

/**
 * Un grupo que se abre al pulsar su título y que está abierto mientras se esté
 * en una de sus páginas: llegar a Recibidos por un enlace y no verla marcada en
 * el menú desorienta.
 */
function GrupoPlegado({ titulo, dentro, children }: { titulo: string; dentro: boolean; children: React.ReactNode }) {
  const [abierto, setAbierto] = useState(dentro);
  useEffect(() => {
    if (dentro) setAbierto(true);
  }, [dentro]);
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className="flex w-full items-center gap-1 rounded-lg px-3 pb-1 text-[11px] font-semibold tracking-wider text-tenue-2 uppercase hover:text-tinta"
      >
        <ChevronRight className={cx("size-3.5 transition-transform", abierto && "rotate-90")} aria-hidden />
        {titulo}
      </button>
      {abierto && children}
    </div>
  );
}
